import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, ArrowUpRight, Plane, Car, Hotel } from "lucide-react";
import { InnerNav } from "../components/InnerNav";
import { Reveal, Overline } from "../components/motion";
import { IndiaMap } from "../components/IndiaMap";
import { useTranslate } from "../context/TranslateContext";
import { ALL_LOCATIONS, ecosystemLinks } from "../mock/data";

export default function FilmLocations() {
  const { t } = useTranslate();
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const listenText = "Follow the film. Every iconic scene was shot somewhere real — from the tea hills of Munnar to the dunes of Jaisalmer. Explore the locations and turn a film into a journey.";

  return (
    <div className="min-h-screen bg-gradient-to-b from-paper to-surface">
      <InnerNav />
      <div className="mx-auto max-w-[1400px] px-6 py-14 md:px-12 md:py-16">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-2xl">
            <Overline>Film Locations of India</Overline>
            <h1 className="mt-3 font-display text-5xl font-semibold tracking-tight text-cream md:text-7xl">
              {t("Follow the ")}<span className="italic text-gradient-gold">{t("film")}</span>
            </h1>
            <p className="mt-4 font-body text-lg text-ash">{t("Every scene has a place. Discover where your favourite films were shot — then plan the trip through our travel partners.")}</p>
          </div>
          
        </div>

        {/* Interactive map + route planner */}
        <div className="mt-12"><IndiaMap /></div>

        <div className="mt-16 space-y-8">
          {ALL_LOCATIONS.map((loc, i) => {
            const links = ecosystemLinks(loc.state);
            return (
              <Reveal key={`${loc.movie.id}-${i}`} delay={(i % 4) * 0.05}>
                <div data-testid={`location-${i}`} className="grid grid-cols-1 gap-6 overflow-hidden rounded-3xl border border-edge bg-surface/40 md:grid-cols-12">
                  <Link to={`/movie/${loc.movie.id}`} className="group relative md:col-span-4">
                    <div className="aspect-[16/10] h-full overflow-hidden md:aspect-auto">
                      <img src={loc.movie.image} alt={loc.movie.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      <div className="absolute inset-0 bg-gradient-to-t from-ink/80 to-transparent" />
                      <div className="absolute bottom-4 left-4">
                        <div className="font-body text-xs uppercase tracking-widest text-gold">{loc.movie.language} · {loc.movie.year}</div>
                        <div className="font-display text-2xl text-paper drop-shadow-lg">{loc.movie.title}</div>
                      </div>
                    </div>
                  </Link>

                  <div className="p-6 md:col-span-8 md:py-8">
                    <div className="flex items-center gap-2 text-gold"><MapPin className="h-5 w-5" />
                      <span className="font-display text-2xl text-cream md:text-3xl">{loc.name}</span>
                    </div>
                    <div className="mt-1 font-body text-sm text-ash">{loc.city}, {loc.state}</div>
                    <p className="mt-3 font-body text-ash">{t(loc.note)}</p>

                    <div className="mt-5 flex flex-wrap gap-3" data-testid={`eco-links-${i}`}>
                      {links.map((l, j) => (
                        <a key={l.url + j} href={l.url} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 rounded-full border border-edge bg-surface px-4 py-2 font-body text-sm text-cream transition-colors hover:border-gold hover:text-gold">
                          {j === 0 ? <MapPin className="h-3.5 w-3.5" /> : j === 1 ? <Hotel className="h-3.5 w-3.5" /> : <Car className="h-3.5 w-3.5" />}
                          {t(l.label)} <ArrowUpRight className="h-3.5 w-3.5" />
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </div>
  );
}
